/**
 * Login service
 * Service for handling user authentication using BFF endpoints
 *
 * This service wraps the global authentication service and provides
 * login-specific functionality for the Login page.
 *
 * BFF Endpoints used:
 * - POST /v1/auth/login - Authenticates user with email and password
 *
 * @see /Volumes/Development/Developer/GroupLinkOne/Projects/BFF/src/modules/auth/auth.controller.ts
 */

import type { LoginRequest, LoginResponse, LoginServiceError } from './types';
import { login as authLogin } from '@/Auth/services/auth.service';

/**
 * Login user with email and password
 *
 * Calls the BFF auth/login endpoint which:
 * - Validates credentials
 * - Sets HTTP-only cookie with JWT token
 * - Returns success status and TTL
 * - Handles account lockout after failed attempts
 *
 * @param credentials - User email and password
 * @returns LoginResponse with success status, TTL, and message
 * @throws LoginServiceError on authentication failure
 *
 * @example
 * ```typescript
 * try {
 *   const response = await loginUser({
 *     email: 'user@grouplinkone.com',
 *     password: 'MyPassword123!'
 *   });
 *   // User authenticated, JWT cookie set, redirect to dashboard
 *   console.log('Login successful, TTL:', response.ttl);
 * } catch (error) {
 *   console.error('Login failed:', error.message);
 * }
 * ```
 */
export const loginUser = async (credentials: LoginRequest): Promise<LoginResponse> => {
  try {
    // Call the global auth service login function
    // This handles:
    // - POST request to /v1/auth/login
    // - Setting TTL in localStorage
    // - Resuming token refresh scheduler
    // - Executing refresh callbacks (profile fetch)
    const response = await authLogin(credentials);
    return response;
  } catch (error: unknown) {
    // Transform error to LoginServiceError
    const loginError: LoginServiceError = {
      message: error instanceof Error ? error.message : 'An unexpected error occurred during login',
      status: (error as { status?: number })?.status,
    };
    throw loginError;
  }
};

/**
 * Validate email format
 * Helper function to check if email is valid before submission
 *
 * @param email - Email address to validate
 * @returns true if email is valid, false otherwise
 */
export const isValidEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

/**
 * Validate password strength
 * Helper function to check if password meets minimum requirements
 *
 * @param password - Password to validate
 * @returns true if password meets requirements, false otherwise
 */
export const isValidPassword = (password: string): boolean => {
  // Minimum 8 characters
  return password.length >= 8;
};
